import { Badge } from "@/components/_atoms/badge";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { DatatableCellTitle } from "@/domain/view/components/datatable/datatable-template-cells/datatable-cell-title";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow } from "@/domain/view/components/datatable/datatable-types";
import { sortRowsByTitle } from "@/domain/view/components/datatable/datatable-utils/sort-rows-by-title";

import { toDisplayDueDate } from "../../lib/due-date";
import type { DataRequest } from "../../models/data-request.model";

const COLUMN_ID_TITLE = "title";
const COLUMN_ID_ENTITY_COUNT = "entityCount";
const COLUMN_ID_DUE_DATE = "dueDate";
const COLUMN_ID_CREATED_BY = "createdBy";
export const COLUMN_ID_STATUS = "status";

export function useDataRequestColumns(): DatatableColumn<DataRequest>[] {
  const { t } = useDomainTranslation("data-request");

  const titleColumn: DatatableColumn<DataRequest> = {
    columnId: COLUMN_ID_TITLE,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => t("list.columns.title"),
    getOrderValue: () => 1,
    getColumnDef: () => ({
      id: COLUMN_ID_TITLE,
      accessorKey: COLUMN_ID_TITLE,
      enableHiding: false,
      enableSorting: true,
      enableColumnFilter: false,
      sortingFn: sortRowsByTitle,
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {t("list.columns.title")}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        return (
          <DatatableCellTitle row={row} className="max-w-120">
            <span className="my-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium">
              {row.original.title}
            </span>
          </DatatableCellTitle>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };

  const entityCountColumn: DatatableColumn<DataRequest> = {
    columnId: COLUMN_ID_ENTITY_COUNT,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => t("list.columns.entityCount"),
    getOrderValue: () => 2,
    getColumnDef: () => ({
      id: COLUMN_ID_ENTITY_COUNT,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {t("list.columns.entityCount")}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        return (
          <span className="px-2 text-sm text-muted-foreground">
            {row.original.entityRequests?.length ?? 0}
          </span>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };

  const dueDateColumn: DatatableColumn<DataRequest> = {
    columnId: COLUMN_ID_DUE_DATE,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => t("list.columns.dueDate"),
    getOrderValue: () => 3,
    getColumnDef: () => ({
      id: COLUMN_ID_DUE_DATE,
      enableHiding: false,
      enableSorting: true,
      enableColumnFilter: false,
      size: 140,
      sortingFn: (rowA, rowB) => {
        if (!isRawRow(rowA.original) || !isRawRow(rowB.original)) return 0;
        const a = rowA.original.dueDate ?? "";
        const b = rowB.original.dueDate ?? "";
        return a.localeCompare(b);
      },
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {t("list.columns.dueDate")}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const dueDate = row.original.dueDate;
        const adjustedDate = dueDate ? toDisplayDueDate(dueDate) : null;
        return (
          <span className="px-2 text-sm text-muted-foreground">
            {adjustedDate ? adjustedDate.toLocaleDateString("de-DE") : "—"}
          </span>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };

  const createdByColumn: DatatableColumn<DataRequest> = {
    columnId: COLUMN_ID_CREATED_BY,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => t("list.columns.createdBy"),
    getOrderValue: () => 4,
    getColumnDef: () => ({
      id: COLUMN_ID_CREATED_BY,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {t("list.columns.createdBy")}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        return (
          <span className="px-2 text-sm text-muted-foreground">
            {row.original.createdBy?.fullname ?? "—"}
          </span>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };

  const statusColumn: DatatableColumn<DataRequest> = {
    columnId: COLUMN_ID_STATUS,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => t("list.columns.status"),
    getOrderValue: () => 5,
    enableSectioning: true,
    getSectionKey: (data) => data.status,
    getColumnDef: () => ({
      id: COLUMN_ID_STATUS,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      header: ({ column }) => (
        <DatatableColumnHeaderCell column={column} className="pr-1 text-xs font-normal">
          {t("list.columns.status")}
        </DatatableColumnHeaderCell>
      ),
      cell: ({ row }) => {
        if (!isRawRow(row.original)) return null;
        const status = row.original.status;
        return (
          <span className="px-2">
            <Badge variant={status === "OPEN" ? "default" : "secondary"}>
              {t(`status.${status}`)}
            </Badge>
          </span>
        );
      },
    }),
    renderDisplayView: () => <></>,
    getFilterConfigurationViewType: () => "none",
    renderFilterConfigurationView: () => null,
    renderAppliedFilterView: () => <></>,
  };

  return [titleColumn, entityCountColumn, dueDateColumn, createdByColumn, statusColumn];
}
